// Tiny technical-style sparkline with light grid + last-point dot.
// Matches the "Operations Deck" mockup chart language.
interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
  showGrid?: boolean;
  showFill?: boolean;
}

export function Sparkline({
  data,
  color = 'var(--color-accent)',
  height = 44,
  showGrid = true,
  showFill = true,
}: SparklineProps) {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 240;
  const h = height;

  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / range) * (h - 6) - 3,
  ] as const);

  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
  const fillD = `${d} L${w},${h} L0,${h} Z`;
  const lastPt = pts[pts.length - 1];

  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {showGrid && (
        <>
          {Array.from({ length: 6 }, (_, i) => (
            <line
              key={`v${i}`}
              x1={(w / 5) * i}
              x2={(w / 5) * i}
              y1="0"
              y2={h}
              stroke="var(--color-border)"
              strokeDasharray="1 2"
              opacity="0.7"
            />
          ))}
          <line
            x1="0"
            x2={w}
            y1={h * 0.5}
            y2={h * 0.5}
            stroke="var(--color-border)"
            strokeDasharray="1 2"
            opacity="0.7"
          />
        </>
      )}
      {showFill && <path d={fillD} fill={color} opacity="0.08" />}
      <path d={d} fill="none" stroke={color} strokeWidth="1.3" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.4" fill={color} />
    </svg>
  );
}
